import { supabase } from '../../lib/supabase'
import type { AvailabilityKind, ExceptionState } from '../agent/availabilityStore'
import { AVAILABILITY_KIND_LABEL, describeWindow } from '../agent/availabilityStore'
import { formatDateRange, getEmployeeTeamMap } from './leaveRequestQueue'

// Every availability exception a signed-in supervisor (or administrator) can
// see, with its latest decision, the same shape as leaveRequestQueue.ts's
// TeamLeaveRequest so the approval inbox reads them side by side.
export type TeamAvailabilityException = {
  id: string
  employeeId: string
  employeeName: string
  teamId: string
  teamName: string
  startDate: string
  endDate: string
  // "Unavailable, 0900 to 1100 (Appointment)": what the inbox prints.
  detail: string
  reason?: string
  state: ExceptionState
  sortDate: string
  decidedAt?: string
  decisionComment?: string
  decidedByName?: string
}

type DecisionRow = { comment: string | null; decided_at: string; decided_by_name: string }

type ExceptionRow = {
  id: string
  employee_id: string
  start_date: string
  end_date: string
  start_time: string | null
  end_time: string | null
  kind: AvailabilityKind
  reason: string | null
  state: ExceptionState
  decisions: DecisionRow[] | null
}

const COLUMNS = 'id, employee_id, start_date, end_date, start_time, end_time, kind, reason, state, decisions(comment, decided_at, decided_by_name)'

const hourMinute = (value: string | null) => (value ? value.slice(0, 5) : null)

export async function getTeamAvailabilityExceptions(): Promise<TeamAvailabilityException[]> {
  const employeeTeams = await getEmployeeTeamMap()

  const { data, error } = await supabase.from('availability_exceptions').select(COLUMNS).order('start_date', { ascending: true })
  if (error || !data) return []

  return (data as unknown as ExceptionRow[]).flatMap((row) => {
    const teamInfo = employeeTeams.get(row.employee_id)
    if (!teamInfo) return []

    // A resubmitted exception can gather a second decision; read the latest.
    const decision = (row.decisions ?? []).reduce<DecisionRow | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    const window = describeWindow(hourMinute(row.start_time) ?? undefined, hourMinute(row.end_time) ?? undefined).toLowerCase()

    return [
      {
        id: row.id,
        employeeId: row.employee_id,
        employeeName: teamInfo.employeeName,
        teamId: teamInfo.teamId,
        teamName: teamInfo.teamName,
        startDate: row.start_date,
        endDate: row.end_date,
        detail: `${AVAILABILITY_KIND_LABEL[row.kind]}, ${window}, ${formatDateRange(row.start_date, row.end_date)}`,
        reason: row.reason ?? undefined,
        state: row.state,
        sortDate: row.start_date,
        decidedAt: decision?.decided_at,
        decisionComment: decision?.comment ?? undefined,
        decidedByName: decision?.decided_by_name,
      },
    ]
  })
}
